import React, { Dispatch, SetStateAction, useEffect } from 'react';
import Modal from '../../components/modal/Modal';
import { Col, convertToKRW, Row } from '../../lib/utils';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { ToggleSwitch } from './ModalCommission';
import { colors } from '../../lib/colors';
import dayjs from 'dayjs';
import { GetClassFeeSettlementTableDataForMembership, MembershipInfo } from '../../services/useMemberQueryService';
import NewColorSquareButton from '../../components/Button/NewColorSquareButton';
import NewWhiteSquareButton from '../../components/Button/NewWhiteSquareButton';
import useMembershipService from '../../services/useMembershipService';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    row?: GetClassFeeSettlementTableDataForMembership;
    year: number;
    month: number;
    commission?: number;
    onPatchMonthCommissionSuccess?: () => void;
};

const ModalMonthCommision = ({ setIsVisible, row, year, month, onPatchMonthCommissionSuccess }: Props) => {
    const membership = row?.membershipInfo?.membership;
    const commission = row?.commission;
    const commissionType = row?.commissionType;
    const BGColor = membership?.BGColor;
    // 활성 여부: 상태 기준 (그룹 수강권은 BGColor가 없을 수 있음)
    const statusLower = (membership?.status as any)?.toLowerCase?.();
    const isActive = statusLower === 'active';
    const isGroup = (membership as any)?.source === 'group';
    const membershipName = membership?.name;
    const startDate = dayjs(membership?.activationDate).format('YYYY-MM-DD');
    const endDate = dayjs(membership?.expirationDate).format('YYYY-MM-DD');
    // 현재회차, 총회차, 회당 가격, 발급일자
    const currentSession = membership?.currentSession;
    const totalSession = membership?.totalSession;
    const pricePerSchedule = membership?.pricePerSchedule;
    const createdAt = dayjs(membership?.createdAt).format('YYYY-MM-DD');

    // 이 수강권에 이번 달 커스텀 요율이 걸려 있는지. 걸려 있을 때만 해제 버튼을 노출한다.
    const isCustomedCommission = !!row?.isCustomedCommission;

    const [deleteErrorMessage, setDeleteErrorMessage] = React.useState<string | null>(null);
    // 저장 실패 안내. 해제 실패와 같은 자리에 같은 방식으로 보여준다(U5).
    const [patchErrorMessage, setPatchErrorMessage] = React.useState<string | null>(null);

    const { patchMonthCommissionMutation, deleteMonthCommissionMutation } = useMembershipService({
        // 저장이 끝난 뒤에 닫는다. 예전에는 클릭 즉시 닫혀서 실패가 화면에 남을 자리가 없었다.
        onPatchMonthCommissionSuccess: () => {
            onPatchMonthCommissionSuccess && onPatchMonthCommissionSuccess();
            setIsVisible(false);
        },
        onPatchMonthCommissionFail: (status) => {
            setPatchErrorMessage(
                status === 404
                    ? '수강권을 찾지 못했어요. 목록을 새로고침한 뒤 다시 시도해주세요.'
                    : status === 403 || status === 412
                      ? '이 수강권의 요율을 변경할 권한이 없어요. 센터 관리자에게 문의해주세요.'
                      : '요율을 저장하지 못했어요. 잠시 후 다시 시도해주세요.',
            );
        },
        // 해제도 저장과 동일하게 목록을 리프레시하고 모달을 닫는다.
        onDeleteMonthCommissionSuccess: () => {
            onPatchMonthCommissionSuccess && onPatchMonthCommissionSuccess();
            setIsVisible(false);
        },
        onDeleteMonthCommissionFail: (status) => {
            setDeleteErrorMessage(
                status === 404
                    ? '되돌릴 설정을 찾지 못했어요. 목록을 새로고침한 뒤 다시 시도해주세요.'
                    : '기본 요율로 되돌리지 못했어요. 잠시 후 다시 시도해주세요.',
            );
        },
    });

    const isDeleting = deleteMonthCommissionMutation.isLoading;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isFocus, setIsFocus] = React.useState(false);
    const [isDisabled, setIsDisabled] = React.useState(false);

    const [value, setValue] = React.useState(commission ?? '');
    // 정산 type
    const [type, setType] = React.useState<'fixed' | 'percentage'>(commissionType ?? 'percentage');

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // 숫자만 입력 가능
        let value = e.target.value;
        const reg = /^[0-9]*$/;
        if (!reg.test(e.target.value)) {
            return;
        }

        // type 이 percentage 일때 100 이상 입력 불가
        if (type === 'percentage' && parseInt(value) > 100) {
            return;
        }
        setValue(value);
    };

    useEffect(() => {
        if (value === '') {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [value]);

    return (
        <Modal
            isCloseOutside
            isCloseButton
            setIsVisible={setIsVisible}
            whiteViewWidth={554}
            borderRadius={25}
            modalAlign="center"
            mt={80}
        >
            <Col className="p-[35px] justify-center items-center">
                <MediumText fontSize={38} lineHeight={55} color="#3D4244" className="mb-[24px] self-start">
                    수강권 정산 비율 설정
                </MediumText>
                <Row
                    className={`border border-solid  rounded-[10px] w-[342px] py-[14px] pl-[21px] pr-[13px] justify-between items-center mb-[14px] cursor-text  hover:border-primary transition-colors duration-300 ease-in-out ${
                        isFocus ? 'border-primary' : 'border-[#E0E0E0]'
                    }`}
                    onClick={() => inputRef.current?.focus()}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        className=" text-[16px] font-noto-regular leading-[23px] "
                        value={value}
                        onChange={onChange}
                        maxLength={type === 'percentage' ? 3 : 6}
                        onFocus={() => setIsFocus(true)}
                        onBlur={() => setIsFocus(false)}
                        placeholder="정산 비율을 입력해주세요"
                        style={{ letterSpacing: '-0.5px' }}
                    />
                    <ToggleSwitch
                        value={type}
                        onToggle={(x) => {
                            setType(x);
                            setValue('');
                        }}
                    />
                </Row>
                <Col className="mb-[32px] w-[342px]">
                    <Col
                        className="flex flex-col w-full rounded-tr-[12px] rounded-tl-[12px] h-[123px] justify-center items-center "
                        style={{
                            background: !isActive
                                ? colors.GREY_200
                                : isGroup
                                    ? 'linear-gradient(135deg, #10B981 0%, #065F46 100%)'
                                    : BGColor && BGColor.length >= 2
                                        ? `linear-gradient(135deg, ${BGColor[0]} 0%, ${BGColor[1]} 100%)`
                                        : 'linear-gradient(135deg, #6B7280 0%, #374151 100%)',
                            alignItems: 'center',
                        }}
                    >
                        <Col className="flex w-full p-[24px] justify-start items-start">
                            <BoldText fontSize={18} lineHeight={28} color="white" className="w-full h-[56px]">
                                {membershipName}
                            </BoldText>
                            <Row className=" items-center">
                                <Col className="bg-white bg-opacity-[34%] rounded-[18px] w-[60px] h-[25px] justify-center items-center mr-[7px] ">
                                    <RegularText fontSize={13} lineHeight={18} color="white">
                                        이용 기간
                                    </RegularText>
                                </Col>
                                <RegularText fontSize={15} lineHeight={21.72} color="white" className=" opacity-[0.6]">
                                    {`${startDate} ~ ${endDate}`}
                                </RegularText>
                            </Row>
                        </Col>
                    </Col>
                    <Col className="w-full h-[183px] border border-solid border-[#D9D9D9] rounded-bl-[21px] rounded-br-[21px] px-[24px] justify-between py-[20px]">
                        <Row className="w-full justify-between">
                            <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                현재 회차
                            </DemiLightText>
                            <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                {currentSession + '회'}
                            </MediumText>
                        </Row>
                        <Row className="w-full justify-between">
                            <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                총 회차
                            </DemiLightText>
                            <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                {totalSession + '회'}
                            </MediumText>
                        </Row>
                        <Row className="w-full justify-between">
                            <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                회당 가격
                            </DemiLightText>
                            <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                {convertToKRW(pricePerSchedule ?? 0) + '원'}
                            </MediumText>
                        </Row>
                        <Row className="w-full justify-between">
                            <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                발급 일자
                            </DemiLightText>
                            <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                {createdAt}
                            </MediumText>
                        </Row>
                    </Col>
                </Col>
                <Col className=" justify-center items-center">
                    <RegularText
                        fontSize={16}
                        lineHeight={23}
                        letterSpacing={-0.32}
                        className="mb-[16px]"
                        color="#505967"
                    >
                        설정한 정산비율은 해당 월부터 계속 적용돼요
                    </RegularText>
                    <Row className=" justify-center items-center">
                        {isCustomedCommission && (
                            <NewWhiteSquareButton
                                text={isDeleting ? '되돌리는 중...' : '기본 요율로 되돌리기'}
                                width={200}
                                height={48}
                                mr={10}
                                onClick={() => {
                                    if (isDeleting) return;
                                    if (!membership?._id) return;
                                    setDeleteErrorMessage(null);
                                    // 저장 경로와 동일한 분기 기준. 그룹이면 membership._id는 MemberTicket._id다.
                                    deleteMonthCommissionMutation.mutate({
                                        source: row?.source,
                                        membershipId: membership?._id,
                                        year: year.toString(),
                                        month: month.toString(),
                                    });
                                }}
                            />
                        )}
                        <NewColorSquareButton
                            text="적용하기"
                            bgColor="primary"
                            textColor="white"
                            disabled={isDisabled || isDeleting}
                            borderRadius={9}
                            width={150}
                            height={48}
                            onClick={() => {
                                if (!membership?._id) return;
                                setPatchErrorMessage(null);
                                // 그룹 수강권은 source='group' 전달 → useMembershipService에서 MemberTicket 전용 엔드포인트로 분기.
                                // row.source가 'group'일 때 membership._id는 MemberTicket._id다.
                                // 모달은 성공 콜백에서 닫는다 — 여기서 닫으면 실패가 보이지 않는다.
                                patchMonthCommissionMutation.mutate({
                                    source: row?.source,
                                    membershipId: membership?._id,
                                    year: year.toString(),
                                    month: month.toString(),
                                    commissionType: type,
                                    commission: (value as number) ?? 0,
                                });
                            }}
                        />
                    </Row>
                    {isCustomedCommission && (
                        <Col className="w-[342px] mt-[12px] items-center">
                            <RegularText
                                fontSize={14}
                                lineHeight={20}
                                letterSpacing={-0.28}
                                className="text-center"
                                color="#505967"
                            >
                                이번 달까지 설정된 커스텀 비율을 모두 삭제하고 기본 요율로 되돌려요. 과거 월 정산
                                표시도 함께 돌아가요.
                            </RegularText>
                        </Col>
                    )}
                    {(patchErrorMessage || deleteErrorMessage) && (
                        <RegularText
                            fontSize={14}
                            lineHeight={20}
                            letterSpacing={-0.28}
                            className="mt-[12px]"
                            color="#FF553E"
                        >
                            {patchErrorMessage || deleteErrorMessage}
                        </RegularText>
                    )}
                </Col>
            </Col>
        </Modal>
    );
};

export default ModalMonthCommision;
